import { useEffect, useState } from "react";
import { FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONVERSATIONS } from "@/constants/testIds";

/** Fix AE (2026-10-19): Excel de los contactos de WhatsApp con los filtros que
 * tiene la lista y, opcional, el periodo del primer mensaje. */
export default function ContactsExportDialog({ open, onOpenChange, params, summary }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setError(""); }, [open]);

  const download = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await api.get("/conversations/export", {
        params: { ...params, ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) },
        responseType: "blob",
      });
      const match = (response.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1] : "Contactos_WhatsApp.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch { detail = null; }
      }
      setError(formatApiErrorDetail(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={CONVERSATIONS.exportDialog}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Descargar Excel</DialogTitle>
          <DialogDescription>Nombre, teléfono, primer y último mensaje, etapa, etiquetas, servicio de interés y anuncio de cada contacto.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm" data-testid={CONVERSATIONS.exportSummary}>
            {summary.length ? `Con los filtros de la lista: ${summary.join(" · ")}` : "Sin filtros: todos los contactos."}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Primer mensaje desde</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={CONVERSATIONS.exportFromInput} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Hasta</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={CONVERSATIONS.exportToInput} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Sin fechas: desde el primer contacto.</p>
          {error && <p className="text-sm text-destructive" data-testid={CONVERSATIONS.exportError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={download} disabled={busy} className="bg-brand hover:bg-brand-hover text-white gap-1.5" data-testid={CONVERSATIONS.exportDownloadButton}>
            <FileDown className="w-4 h-4" /> {busy ? "Generando..." : "Descargar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
